import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import { AuthContext } from "@/taskpane/contexts/auth/AuthContext";
import { VariableSizeList } from "react-window";
import { useNavigate } from "react-router-dom";
import { Folder, SubFolder, SubFolderData } from "@/taskpane/types/folder";
import type { Office } from "@/taskpane/services/sso";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { WarningTooltip } from "@/taskpane/utils/tooltips";
import { DOSSIER_STATUS_INOT } from "@/taskpane/config/label";
import { getAttachments } from "@/taskpane/utils/attachments";
import { usePolling } from "@/taskpane/services/polling.hook";
import { useGetUserDataQuery } from "@/taskpane/services/user.hook";
import { useLocalStorage, useWindowSize } from "@uidotdev/usehooks";
import { useGetUserAttributesQuery } from "@/taskpane/services/auth.hook";
import { CloseOutlined, SyncAlt as SyncAltIcon, EmailOutlined, HelpOutline, ArrowRightAlt, Refresh, AccountCircle, Logout, SwapHoriz } from "@mui/icons-material";
import { useOfficeContext } from "@/taskpane/contexts/office/office-context";
import { ControlCenterIcon } from "@/taskpane/components/icons/control-center";
import { SelectInitialesDialog } from "@/taskpane/modules/folders/SelectInitialesModal";
import { useGetFoldersQuery, useGetSubFolderDetailsQuery, useGetSubFoldersQuery, useSetSousDossierAutoImportStatus } from "@/taskpane/services/folders.hook";
import { getSubFolders } from "@/taskpane/services/folders";
import { updateSousDossierRagStatus } from "@/taskpane/services/chat";
import { alpha, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Grid, IconButton, List, ListItemButton, PaletteColor, Skeleton, Stack, styled, TextField, Theme, Tooltip, tooltipClasses, Typography, useTheme, Drawer, ListItem,  ListItemText,  Badge, Switch, SwitchProps, Collapse, Menu, MenuItem, ListItemIcon } from "@mui/material";
import LaunchIcon from '@mui/icons-material/Launch';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import { CLIENT_API_ALLOW_TO_AUTO_IMPORT } from "@/taskpane/config/client-allow-to-auto-import";
import styles from "./FoldersList.styles"
import type { SyncStatusTooltipProps, SyncStatus } from "./FoldersList.types";
import { AiButton } from "@/taskpane/components/ai-chat/AiButton";
import { PaletteSousDossier } from "@/taskpane/components/sous-dossier-selector";
import { router } from "@/taskpane/AppRoutes";
import { isRunningInOutlook } from "@/taskpane/services/office";
import { getAuthTokens } from "@/taskpane/utils/client";
import { SnackbarCloseIcon } from "@/taskpane/components/snackbar-provider";
import { enqueueSnackbar, SnackbarKey, closeSnackbar } from "notistack";
import { BsGear } from "react-icons/bs";
import AutoImportStatusSwitch from "@/taskpane/components/button/ImportSwitch";
import { StyledTooltipIcon } from "../ai-chat/AiChatPage";
import { styled as muiStyled } from "@mui/material/styles";

const StyledList = muiStyled(VariableSizeList as any)({
  backgroundColor: "white",
  border: "solid 1px",
  borderColor: "rgba(135, 135, 135, 0.34)",
  borderRadius: "4px",
  overflowX: "hidden",

  // By adding padding and using content-box, we force the scrollbar
  // to appear in a clipped area, making it an overlay.
  paddingRight: '15px',
  boxSizing: 'content-box',

  // --- Custom Scrollbar Styles (for supported browsers like Chrome/Safari) ---
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    borderRadius: '4px',
    transition: 'background-color 0.3s',
  },
  '&:hover::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  // For Firefox
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(0, 0, 0, 0.25) transparent',
});

const UncheckedIcon = (
  <Box
    sx={{
      width: 25,
      height: 16,
      border: (theme) => `2px solid ${theme.palette.action.disabled}`,
      borderRadius: '6px',
      backgroundColor: 'transparent',
    }}
  >
    <svg width="10" height="10" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    </svg>
  </Box>
  
);

const CheckedIcon = (
  <Box
    sx={{
      width: 25,
      height: 16,
      backgroundColor: 'primary.main',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: (theme) => `2px solid ${theme.palette.primary.main}`,
    }}
  >
    <svg width="10" height="10" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.8125 11.3125L0.84375 6.34375L2.21875 4.96875L5.8125 8.5625L14.0312 0.34375L15.4062 1.71875L5.8125 11.3125Z" fill="white"/>
    </svg>
  </Box>
);

/**
 * @description Renders a single folder item in the folders list.
 *
 * @param {Object} props - The props for the component.
 * @param {Folder} props.folder - The folder object to render.
 * @param {() => void} props.onClick - The click event handler for the folder item.
 * @param {boolean} props.selected - Indicates whether the folder item is selected.
 *
 * @returns {JSX.Element} The rendered component.
 */
function FolderItem({ folder, onClick, selected, onConfigureAutoImport, expandedFolders, onToggleExpanded, onSelectedSousDossier, highlightedSubFolder }: { 
  folder: Folder; 
  onClick: () => void; 
  selected: boolean;
  onSelectedSousDossier: (sousDossier: SubFolder) => void;
  onConfigureAutoImport: (folder: Folder) => void;
  expandedFolders: Set<string>;
  onToggleExpanded: (folderId: string | null) => void;
  highlightedSubFolder: SubFolder | null;
}) {
  const navigate = useNavigate();
  

  const { data: userData } = useGetUserDataQuery();

  const theme = useTheme();
  const queryClient = useQueryClient();


  /**
   * @description Fetch subfolders data on render of the component.
   * @description Keep subfolder data fresh for 10 minutes.
   * @description This will prevent refetching for every item on screen.
   */
  const {
    data: subFoldersData,
    isLoading: subFoldersLoading,
    refetch: refetchSubFolders,
  } = useGetSubFoldersQuery(folder?.dossier_id || "", {
    staleTime: 1000 * 60 * 10,
    placeholderData: (prevData) => prevData,
  });

  const setSousDossierAutoImportStatusMutation = useSetSousDossierAutoImportStatus({
    onMutate: async (newData: { dossierId: string, sousDossierId: string, autoImportStatusValue: boolean }) => {
      await queryClient.cancelQueries({ queryKey: ['getSubFolders', newData.dossierId] });
      const previousData = queryClient.getQueryData<{ sous_dossiers: SubFolder[] }>(['getSubFolders', newData.dossierId]);

      if (previousData) {
        queryClient.setQueryData<{ sous_dossiers: SubFolder[] }>(['getSubFolders', newData.dossierId], (old) => {
          if (!old || !Array.isArray(old.sous_dossiers)) {
            return old;
          }
          
          const updatedSousDossiers = old.sous_dossiers.map(sf => 
            sf.sous_dossier_id === newData.sousDossierId
              ? { ...sf, sous_dossier_import_partenaire_auto: newData.autoImportStatusValue ? 'sync' : 'unsync' }
              : sf
          );
          
          return { ...old, sous_dossiers: updatedSousDossiers };
        });
      }
      return { previousData };
    },
    onError: (error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['getSubFolders', variables.dossierId], context.previousData);
      }
      enqueueSnackbar(
        typeof error === "string"
          ? error
          : (error?.message ?? "Une erreur est survenue lors de la mise a jour du statut d'import automatique"),
        {
          variant: "error",
          persist: true,
          action: (snackbarId: SnackbarKey | undefined) => (
            <SnackbarCloseIcon handleClose={() => closeSnackbar(snackbarId)} />
          ),
        }
      );
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['getSubFolders', variables.dossierId] });
      queryClient.invalidateQueries({ queryKey: ['getFolders'] });
    },
  });


  /**
   * @description Computes the synchronization status of the folder based on the presence and value of the key: dossier_import_partenaire_auto on the dossier object.
   *
   * @memoization
   * @dependency {folder?.dossier_id} - The ID of the dossier.
   * @dependency {subFoldersData} - The list of sous-dossiers.
   *
   * @returns {string} The synchronization status of the folder. Either "loading", "synced", "unsync", or "partialSynced".
   */
  const syncStatus = useMemo(() => {
    if (subFoldersData && subFoldersData.length === 1) {
      const autoImportValue = subFoldersData[0].sous_dossier_import_partenaire_auto;
      return (autoImportValue === 'sync' || autoImportValue === true) ? 'synced' : 'unsync';
    }

    const synced = folder && folder?.dossier_import_partenaire_auto_status === "sync";
    if (synced) {
      return "synced";
    }
    const partialSynced = folder && folder?.dossier_import_partenaire_auto_status === "partial";
    if (partialSynced) {
      return "partialSynced";
    }
    const unsynced = !folder || !folder?.dossier_import_partenaire_auto_status || folder?.dossier_import_partenaire_auto_status === "unsync";
    if (unsynced) {
      return "unsync";
    }
    return "synced";

  }, [folder?.dossier_id, folder?.dossier_import_partenaire_auto_status, subFoldersData]);

  const shouldRenderAutoImportSwitch = useMemo(() => {
    if (userData?.user_info?.client_api && CLIENT_API_ALLOW_TO_AUTO_IMPORT.includes(userData.user_info.client_api)) {
      return true;
    } else {
      return false;
    }
  }, [userData]);

  /**
   * @description Computes the props for the sync icon based on the sync status.
   *
   * @memoization
   * @dependency {syncStatus} - The synchronization status of the folder. Either "loading", "synced", "unsync", or "partialSynced".
   *
   * @returns {SyncStatus} The props for the sync icon.
   */
  const getSyncStatus = useMemo(() => {
    switch (syncStatus) {
      case "synced":
        return {
          label: "Analyse automatique des documents présents dans le logiciel métier activée",
          color: "success",
          value: "synced",
        } as SyncStatus;
      case "unsync":
        return {
          label: "Pas d'analyse automatique des documents présents dans le logiciel métier",
          color: "error",
          value: "unsync",
        } as SyncStatus;
      case "partialSynced":
        return {
          label:
            "Analyse automatique des documents présents dans le logiciel métier restreint à certains sous-dossiers",
          color: "warning",
          value: "partialSynced",
        } as SyncStatus;
      default:
        return { label: "N/A", color: "secondary", value: "loading" } as SyncStatus;
    }
  }, [syncStatus]);

  const handleConfigureAutoImport = useCallback((folder: Folder) => {
    onConfigureAutoImport(folder);
  }, [onConfigureAutoImport]);

  const isExpanded = expandedFolders.has(folder.dossier_id);
  const hasMultipleSubfolders = subFoldersData?.length && subFoldersData?.length > 1;
  const hasSingleSubfolder = subFoldersData?.length === 1;

  const handleArrowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMultipleSubfolders) {
      onToggleExpanded(folder.dossier_id);
    } else {
      onToggleExpanded(null);
    }
  };

  return (
    <>
    <ListItemButton
      sx={styles.listItemButton(theme, alpha, hasMultipleSubfolders as boolean)}
      onClick={(e) => {
        handleArrowClick(e);
        onClick();
      }}
      selected={selected}
    >
      <Grid container alignItems="center">

        {/* First column: Name and creation date */}
        <Grid item xs={9}>
          <Stack direction="row" alignItems="space-between" spacing={1}>

            {/* First column: Sync status icon */}
            {subFoldersLoading ? (
              <Box sx={{ p: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer', width: 47, justifyContent: 'center' }}>
                <CircularProgress size={19}/>
              </Box>
            ) : shouldRenderAutoImportSwitch ? (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hasSingleSubfolder && subFoldersData) {
                      const subFolder = subFoldersData[0];
                      setSousDossierAutoImportStatusMutation.mutate({
                        dossierId: folder.dossier_id,
                        sousDossierId: subFolder.sous_dossier_id,
                        autoImportStatusValue: !subFolder.sous_dossier_import_partenaire_auto,
                      });
                    } else {
                      handleConfigureAutoImport(folder);
                    }
                  }}
                  sx={{ p: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                >
                  {hasMultipleSubfolders ? (
                    <StyledTooltipIcon placement="bottom" tooltipTitle="Ouvrir le dossier">
                      <ArrowForwardIosIcon sx={{
                        marginTop: "4px",
                        marginLeft: "4px",
                        marginRight: "4px",
                        fontSize: "19px",
                        fontWeight: "bold",
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifySelf: 'center',
                        alignSelf: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease-in-out',
                      }}
                      onClick={(e) => {
                        handleArrowClick(e);
                        onClick();
                      }}
                      />
                    </StyledTooltipIcon>
                  ) : (
                    <StyledTooltipIcon placement="bottom" tooltipTitle={`${syncStatus === 'synced' ? 'Désactiver l’analyse automatique' : 'Activer l’analyse automatique'}`}>
                    <AutoImportStatusSwitch
                      checked={syncStatus === 'synced' || syncStatus === 'partialSynced'}
                      sx={{
                        ...(syncStatus === 'partialSynced' && {
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: theme.palette.warning.main,
                          },
                        }),
                      }}
                    />
                  </StyledTooltipIcon>
                  )}
                </Box>
            ) : null}

            <Stack sx={styles.dossierNameAndCreationDateContainer}>

              {/* First column: Folder name */}
              <Stack direction="row">
                <Tooltip title={folder?.name?.length > 30 ? folder?.name : ""}>
                <Typography
                    variant="caption"
                    fontWeight={500}
                    color="#09226b"
                    fontSize="10px"
                    sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    fontFamily: "Inter, Helvetica",
                    }}
                    >
                    {folder?.name ?? "N/A"}
                  </Typography>
                </Tooltip>
              </Stack>

              {/* First column: Folder creation date */}
              <Stack direction="row" spacing={0} mt={"2px"}>  
                {subFoldersData?.length && subFoldersData?.length > 1 ? (
                <Typography noWrap fontSize={8} fontWeight="semibold" color="black" sx={styles.creationDate}>
                  Multiple sous-dossiers
                </Typography>
                ) : (
                  <Typography
                  variant="caption"
                  color="#09226b"
                  fontWeight="medium"
                  sx={{
                    width: "271px",
                    height: "11px",
                    fontSize: "9px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontFamily: "Inter, Helvetica",
                    fontStyle: "normal",
                    lineHeight: "normal",
                  }}
                  >
                  {subFoldersData?.[0]?.nature}
                </Typography>
              )}
              </Stack>
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={3} sx={{ pl: 0, display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
          <Grid container alignItems="center" justifyContent="flex-end" spacing={1} wrap="nowrap">
            {/* Third column: ID and status */}
            <Grid item>
              <Stack alignItems="flex-end">

              {selected && hasSingleSubfolder ? (
                <StyledTooltipIcon placement="bottom" tooltipTitle="Ouvrir le centre de contrôle">
                  <IconButton
                    size="large"
                    color="inherit"
                    sx={styles.controlCenterIcon(theme)}
                    edge="start"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/folder/${folder.dossier_id}/details/${subFoldersData?.[0]?.sous_dossier_id}`);
                    }}
                  >
                    <BsGear color={folder?.en_attente_choix_filiation_dossier ? "warning" : "inherit"} fontSize="small" size={20} />
                  </IconButton>
                </StyledTooltipIcon>
                ) : (
                <Stack direction="row" spacing={1}>
                {folder.en_attente_choix_filiation_dossier && subFoldersData?.length === 1 && (
                  <Tooltip title="En attente de sélection des biens">
                    <Box
                      alignSelf="center"
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        backgroundColor: 'orange',
                        borderRadius: '50%',
                        marginLeft: 1,
                      }}
                    />
                  </Tooltip>
                )}
                <Box sx={{
                  display: 'flex',
                  width: '2.6875rem',
                  height: '1.4375rem',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}>
                  <Typography color="black" fontWeight="bold" fontSize={11} noWrap sx={{
                    overflow: "hidden",
                    color: "#09226B",
                    opacity: 0.5,
                    textOverflow: "ellipsis",
                    fontFamily: "Inter",
                    fontSize: "0.5625rem",
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "normal",
                  }}>
                    {folder?.dossier_id ?? "N/A"}
                </Typography>
                </Box>
                </Stack>
               )}
              </Stack>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ListItemButton>
    <Box sx={{
      width: '100%',
      px: '8px',
      backgroundColor: 'white',
      borderBottomLeftRadius: '8px',
      borderBottomRightRadius: '8px',
      mt: 0,
      mb: "-15px",
      boxShadow: `
        inset 0 4px 6px -3px rgba(0, 0, 0, 0.3), 
        inset 4px 0 6px -3px rgba(0, 0, 0, 0.3), 
        inset -4px 0 6px -3px rgba(0, 0, 0, 0.3)
      `,
    }}>
    {subFoldersData?.length && subFoldersData?.length > 1 && (
      <Collapse sx={{width: '100%', px: '1px', mt: 0, mb: "-15px"}} in={isExpanded} timeout="auto" unmountOnExit>
        <SousDossierAutoImportList
          open={isExpanded}
          onClose={() => onToggleExpanded(folder.dossier_id)}
          folder={folder}
          subFolders={subFoldersData}
          onSelectedSousDossier={onSelectedSousDossier}
          highlightedSubFolder={highlightedSubFolder}
        />
      </Collapse>
    )}
    </Box>
    </>
  );
}

/**
 * Component to display a message when no folders are found.
 * @returns {JSX.Element} The rendered component.
 */
function NoFolders() {
  return (
    <Typography fontWeight="bold" color="grey.400" textAlign="center" p={3}>
      Aucun dossier correspondant à ces critères n'a été trouvé
    </Typography>
  );
}


/**
 * @description Main component to render the list of folders.
 * @returns {JSX.Element} The rendered component.
 */
export default function FolderList() {
  const { isAuthenticated, tokens, logout } = useContext(AuthContext);
  const [search, setSearch] = useState("");
  const [showMyFolders, setShowMyFolders] = useState(false);
  const [showInProgress, setShowInProgress] = useState(false);
  const [showFiliationChoiceButton, setShowFiliationChoiceButton] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<Folder>();
  const [currentEmailId, setCurrentEmailId] = useState<string | undefined>(undefined);
  const [hasAttachments, setHasAttachments] = useState(false);
  const [listHeight, setListHeight] = useState(0);
  const [isFiliationEmail, setIsFiliationEmail] = useState(false);
  const [filiationInfo, setFiliationInfo] = useState<{folderId?: string, subFolderId?: string} | null>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const isDevEnvironment = useMemo(() => {
    const apiUrl = process.env.REACT_APP_BASE_URL_API_ADDIN || '';
    return apiUrl.includes('dev');
  }, []);
  const isOutlook = isRunningInOutlook();
  const [refreshCount, setRefreshCount] = useLocalStorage<number>("refreshCount", 0);
  const [isPopupBlockFor24Hour, setIsPopupBlockFor24Hour] = useLocalStorage<boolean | null>("isPopupBlockFor24Hour", null);
  const [isPopupBlockFor24HourSince, setIsPopupBlockFor24HourSince] = useLocalStorage<number | null>("isPopupBlockFor24HourSince", null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedSubFolder, setSelectedSubFolder] = useState<SubFolder[] | null>(null);
  const [highlightedSubFolder, setHighlightedSubFolder] = useState<SubFolder | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [subfolderCounts, setSubfolderCounts] = useState<Record<string, number>>({});
  const size = useWindowSize();
  const [autoImportModalFolder, setAutoImportModalFolder] = useState<Folder | undefined>(undefined);
  const { data: subFoldersForModal } = useGetSubFoldersQuery(autoImportModalFolder?.dossier_id || "", { enabled: !!autoImportModalFolder });

  const { changeCounter, currentMailbox, setFiliationData } = useOfficeContext();

  const { isPolling, setPollingConfig, startPolling } = usePolling();

  /**
   * @description Fetch folders data on render of the component.
   * @description Keep folder list fresh for 10 minutes.
   * @description This will prevent refetching for every item on screen.
   */
  const { data: foldersData, isLoading, refetch: refetchFolders } = useGetFoldersQuery({
    enabled: isAuthenticated && !!tokens?.IdToken,
    // Keep folder list fresh for 10 minutes
    staleTime: 1000 * 60 * 10,
  });

  /**
   * @description Fetch user data on render of the component.
   * @description Keep user data fresh for 10 minutes.
   * @description This will prevent refetching for every item on screen.
   */
  const { data: userData, isLoading: userDataLoading, error: userDataError } = useGetUserDataQuery({
    enabled: isAuthenticated && !!tokens?.IdToken,
    // User data is unlikely to change often during a session
    staleTime: 1000 * 60 * 10,
  });

  const { data: dataAttachments, refetch } = useQuery({
    queryKey: ["getAttachments", currentEmailId],
    queryFn: getAttachments,
    enabled: !!currentEmailId,
  });
  
  const hasValidAccessToken = Boolean(tokens?.AccessToken && tokens.AccessToken.length > 0);
  const { data: currentUser, isLoading: isLoadingCurrentUser } = useGetUserAttributesQuery({
    enabled: hasValidAccessToken,
    // User attributes are also stable
    staleTime: 1000 * 60 * 10,
  });
  const { data: sousDossiersDataFromQuery, isLoading: isLoadingSousDossiers } = useGetSubFoldersQuery(
    selectedFolderId,
    {
      // Keep this data fresh as well
      staleTime: 1000 * 60 * 10,
    }
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  useEffect(() => {
    if (selectedFolderId) {
      if (sousDossiersDataFromQuery?.length === 1) {
        setSelectedSubFolder(sousDossiersDataFromQuery);
      } else { 
        setSelectedSubFolder(null);
      }
    } else {
      setSelectedSubFolder(null);
    }
  }, [selectedFolderId, isLoadingSousDossiers]);

  const handleItemAction = async (item: SubFolder) => {
    if (isOutlook) {
      // Get the current authentication token to pass to the browser
      const tokens = await getAuthTokens();
      
      // Include all three tokens in the URL for complete login transfer
      const params = new URLSearchParams();
      if (tokens) {
        if (tokens.IdToken) params.append('idToken', tokens.IdToken);
        if (tokens.AccessToken) params.append('accessToken', tokens.AccessToken);
        if (tokens.RefreshToken) params.append('refreshToken', tokens.RefreshToken);
        if (tokens.ExpiresIn) params.append('expiresIn', tokens.ExpiresIn.toString());
      }
      
      // Add tenant_id parameter for automatic office switch
      const selectedOfficeTenantId = localStorage.getItem('selected_office_tenant_id');
      if (selectedOfficeTenantId) {
        params.append('tenantId', selectedOfficeTenantId);
      }
      
      // Add crpcen parameter for automatic office switch (if available)
      const selectedOfficeCrpcen = localStorage.getItem('selected_office_crpcen');
      if (selectedOfficeCrpcen) {
        params.append('crpcen', selectedOfficeCrpcen);
      }

      // Determine base URL based on environment
      const apiUrl = process.env.REACT_APP_BASE_URL_API_ADDIN || '';
      const redirectUri = process.env.REACT_APP_SSO_REDIRECT_URI || '';
      const isLocalDev = apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1') || 
                         redirectUri.includes('localhost') || redirectUri.includes('127.0.0.1');
      
      // Point to localhost if we're in local development
      const baseUrl = isLocalDev 
        ? 'https://localhost:3001'
        : (isDevEnvironment ? 'https://dev-addin.bylaw.fr' : 'https://addin.bylaw.fr');

      // With hash router, query params must be BEFORE the hash
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const url = `${baseUrl}/${queryString}#/folder/${selectedFolderId}/${item.sous_dossier_id}/chat`;

      Office.context.ui.openBrowserWindow(url);

    } else {
      router.navigate(`/folder/${selectedFolderId}/${item.sous_dossier_id}/chat`);
    }
  };

  const handleSubFolderIndexRequest = async (dossierId: string, subFolderToUpdate: SubFolder): Promise<boolean> => {
    const originalSubFolders = selectedSubFolder ? [...selectedSubFolder] : [];
    
    // Optimistic UI Update
    setSelectedSubFolder(prevSubFolders => 
      (prevSubFolders || []).map(sf =>
        sf.sous_dossier_id === subFolderToUpdate.sous_dossier_id
          ? {
              ...sf,
              sous_dossier_rag_indexed_status: 'requested',
              sous_dossier_rag_indexed_requested_on: new Date().toISOString(), // Ensure this is UTC
            }
          : sf
      )
    );

    try {
      // API Call
      await updateSousDossierRagStatus({ 
        dossier_id: dossierId,
        sous_dossier_id: subFolderToUpdate.sous_dossier_id,
        index_request: 'requested',
      });
      return true; // Indicate success
    } catch (error) {
      console.error(`Optimistic update failed for ${subFolderToUpdate.sous_dossier_id}, reverting.`, error);
      // Revert UI on error
      setSelectedSubFolder(originalSubFolders);
      return false; // Indicate failure
    }
  };

  const folders = foldersData?.dossiers || [];

  const updateCurrentEmail = useCallback(async () => {
    if (currentMailbox?.item) {
      const newEmailId = currentMailbox.item.itemId;
      setCurrentEmailId(newEmailId);
    }
  }, [setCurrentEmailId, currentMailbox]);

  /**
   * @description Checks if the current email is a filiation email.
   * 
   * @returns {Promise<void>} A promise that resolves when the check is complete.
   */
  const checkForFiliationEmail = useCallback(() => {
    if (!currentMailbox?.item) {
      setIsFiliationEmail(false);
      setFiliationInfo(null);
      return;
    }

    const { body } = currentMailbox.item;
    
    body.getAsync(Office.CoercionType.Html, function (asyncResult: any) {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        const bodyHtml = asyncResult.value;
        
        const element = document.createElement("div");
        element.innerHTML = bodyHtml;
        
        const jsonData = element.querySelector("#filiation-data, #x_filiation-data")?.textContent;
        
        if (!jsonData) {
          setIsFiliationEmail(false);
          setFiliationInfo(null);
          return;
        }
        
        try {
          const filiationData = JSON.parse(jsonData);
          const clientId = filiationData?.client_id;
          const folderId = filiationData?.dossier_id;
          const subFolderId = filiationData?.flow_id;
          
          if (!clientId || !folderId || !subFolderId) {
            setIsFiliationEmail(false);
            setFiliationInfo(null);
            return;
          }
          
          // Store filiation data in context for use in redirection
          setFiliationData({ ...filiationData });
          
          // Set local state for UI display
          setIsFiliationEmail(true);
          setFiliationInfo({ folderId, subFolderId });
        } catch (error) {
          console.error("[FoldersList] Error parsing filiation data:", error);
          setIsFiliationEmail(false);
          setFiliationInfo(null);
        }
      } else {
        setIsFiliationEmail(false);
        setFiliationInfo(null);
      }
    });
  }, [currentMailbox, setFiliationData]);

  /**
   * @description Filters the folders based on the provided criteria.
   *
   * @param {Folder[]} folders - The list of folders to filter.
   * @param {boolean} showMyFolders - Indicates whether to show only the user's folders.
   * @param {boolean} showInProgress - Indicates whether to show only folders in progress.
   * @param {string} search - The search term to filter folders by name or dossier ID.
   * @param {UserData} userData - The user data containing information about the user.
   *
   * @returns {Folder[]} - The filtered list of folders.
   */
  const filteredFolders = useMemo(() => {
    let data = folders ?? [];

    if (showMyFolders) {
      const userInitials = userData?.user_info?.initiales_inot?.toUpperCase();
      if (userInitials) {
        data = data.filter((folder) => folder.intervenants_notaires?.includes(userInitials) || folder.dossier_intervenants_notaire?.includes(userInitials));
      } else {
        return []; // No initials, so "my folders" is empty
      }
    }
    

    if (showInProgress) {
      data = data.filter((folder) => folder.status === "en_cours");
    }

    if (search) {
      const lowerCaseSearch = search.toLowerCase();
      data = data.filter(
        (folder) =>
          folder.name?.toLowerCase().includes(lowerCaseSearch) ||
          folder.dossier_id?.toLowerCase().includes(lowerCaseSearch)
      );
    }

    return data;
  }, [folders, showMyFolders, showInProgress, search, userData]);

  /**
   * @description Computes for each render if the modal allowing the user to select their initials should be displayed.
   *
   * @dependency {isPolling} - Indicates whether the polling is active.
   * @dependency {currentUser} - The current user data. If the user has no partner ID, that's meaning that the user has not selected or found their initials yet.
   */
  const shouldDisplaySelectionUserInfosModal = useMemo(() => {
    return !isLoadingCurrentUser && !currentUser?.partenaireId && !isPolling
  }, [isPolling, currentUser, isLoadingCurrentUser]);

  /**
   * @description Computes for each render if the modal allowing the user to select their initials should be block during 24 hour because of exceed the limit of refresh count (2 times).
   *
   * @dependency {refreshCount} -
   * @dependency {isPopupBlockFor24Hour} -
   * @dependency {isPopupBlockFor24HourSince} -
   */
  const shouldBlockSelectionUserInfosModal = useMemo(() => {
    if (refreshCount >= 2 && isPopupBlockFor24Hour && isPopupBlockFor24HourSince) {
      const now = Date.now();
      const diffBetweenNowAndIsBlockedSinceInMs = now - isPopupBlockFor24HourSince;
      const diffBetweenNowAndIsBlockedSinceInSeconds = diffBetweenNowAndIsBlockedSinceInMs / 1000;
      const diffBetweenNowAndIsBlockedSinceIsLessThan24Hour = diffBetweenNowAndIsBlockedSinceInSeconds <= 24 * 60 * 60;

      if (diffBetweenNowAndIsBlockedSinceIsLessThan24Hour) {
        return true;
      } else {
        setRefreshCount(0);
        setIsPopupBlockFor24Hour(false);
        setIsPopupBlockFor24HourSince(null);

        return false;
      }
    } else {
      return false;
    }
  }, [refreshCount, isPopupBlockFor24Hour, isPopupBlockFor24HourSince, setRefreshCount, setIsPopupBlockFor24Hour, setIsPopupBlockFor24HourSince]);

  const displayModal = shouldBlockSelectionUserInfosModal ? false : shouldDisplaySelectionUserInfosModal;


  /**
   * @description Effect hook to update the current email ID and refetch the attachments when the current email ID changes.
   *
   * @effect
   * @dependency {updateCurrentEmail} - The function to update the current email ID.
   */
  useEffect(() => {
    updateCurrentEmail();
    const intervalId = setInterval(() => {
      updateCurrentEmail();
    }, 5000); // Check for updates every 5 seconds
    return () => clearInterval(intervalId);
  }, [updateCurrentEmail]);

  /**
   * @description Effect hook to refetch the attachments when the current email ID changes.
   *
   * @effect
   * @dependency {currentEmailId} - The current email ID.
   */
  useEffect(() => {
    if (currentEmailId) {
      refetch();
    }
  }, [currentEmailId, refetch]);

  /**
   * @description Effect hook to update the hasAttachments state based on the dataAttachments.
   *
   * @effect
   * @dependency {dataAttachments} - The attachments data.
   */
  useEffect(() => {
    setHasAttachments(!!dataAttachments && dataAttachments?.attachments && dataAttachments?.attachments?.length > 0);
  }, [dataAttachments]);

  /**
   * @description Effect hook to update the current email ID when the changeCounter or currentMailbox changes.
   *
   * @effect
   * @dependency {changeCounter} - The change counter.
   * @dependency {currentMailbox} - The current mailbox.
   */
  useEffect(() => {
    updateCurrentEmail();
    checkForFiliationEmail();
  }, [changeCounter, currentMailbox, checkForFiliationEmail]);

  // Update list height when container size changes
  useEffect(() => {
    const updateHeight = () => {
      if (listContainerRef.current) {
        setListHeight(listContainerRef.current.clientHeight);
      }
    };

    // Initial height
    updateHeight();

    // Create ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(updateHeight);
    if (listContainerRef.current) {
      resizeObserver.observe(listContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  /**
   * @description Effect hook to update the selected folder when the filtered folders change.
   *
   * @effect
   * @dependency {selectedFolder} - The currently selected folder.
   * @dependency {filteredFolders} - The filtered list of folders.
   */
  useEffect(() => {
    if (!selectedFolder) {
      return;
    }
    if (!filteredFolders.find((f) => f.dossier_id === selectedFolder.dossier_id)) {
      setSelectedFolder(undefined);
      setSelectedFolderId(null);
      setSelectedSubFolder(null);
      setHighlightedSubFolder(null);
    }
   
  }, [selectedFolder, filteredFolders]);

  /**
   * @description Effect hook to check the list of dossiers for the key en_attente_choix_filiation_dossier.
   *
   * @effect
   * @dependency {foldersData} - The data containing the list of dossiers.
   */


  const shouldOverrideRenderInitialesDialog = useMemo(() => {
    if (userData?.user_info?.client_api && CLIENT_API_ALLOW_TO_AUTO_IMPORT.includes(userData.user_info.client_api)) {
      return true;
    } else {
      return false;
    }
  }, [userData]);

  const handleConfigureAutoImport = useCallback((folder: Folder) => {
    setAutoImportModalFolder(folder);
  }, []);

  const listRef = useRef<VariableSizeList>(null);
  const queryClient = useQueryClient();

  

  const handleToggleExpanded = useCallback(async (folderId: string | null) => {
    if (folderId === null) {
      setExpandedFolders(new Set());
      setSelectedFolderId(null);
      setSelectedFolder(undefined);
      setSelectedSubFolder(null);
      setHighlightedSubFolder(null);
      return;
    }
    const newSet = new Set(expandedFolders);
    const isCurrentlyExpanded = newSet.has(folderId);


    if (isCurrentlyExpanded) {
        newSet.delete(folderId);
        setSelectedFolderId(null);
        setSelectedFolder(undefined);
        setSelectedSubFolder(null);
        setHighlightedSubFolder(null);
        setExpandedFolders(newSet);
    } else {
        try {
            const subFoldersData = await queryClient.fetchQuery({
                queryKey: ['getSubFolders', folderId], 
                queryFn: () => getSubFolders(folderId)
            });
            const count = subFoldersData?.sous_dossiers?.length || 0;
            
            setSubfolderCounts(prev => ({ ...prev, [folderId]: count }));

            newSet.add(folderId);
            setExpandedFolders(newSet);
        } catch (error) {
            console.error("Failed to fetch subfolders, cannot expand:", error);
        }
    }
  }, [expandedFolders, queryClient]);

  useEffect(() => {
    // When a search is active, collapse all folders to prevent layout issues.
    if (search) {
      setExpandedFolders(new Set());
      setSubfolderCounts({});
      setSelectedFolderId(null);
      setSelectedFolder(undefined);
      setSelectedSubFolder(null);
      setHighlightedSubFolder(null);
    }
  }, [search]);

  // This effect will run whenever the expanded folders change,
  // telling react-window to re-calculate the heights.
  const handleSelectedSousDossier = useCallback((sousDossier: SubFolder) => {
    setHighlightedSubFolder(sousDossier);
  }, []);

  useEffect(() => {
    if (highlightedSubFolder?.sous_dossier_id) {
      setSelectedSubFolder([highlightedSubFolder]);
    }
  }, [highlightedSubFolder]);


  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [expandedFolders]);

  /**
   * @description Gets the height for a specific item in the list
   * @param index - The index of the item
   * @returns The height of the item
   */
  const getItemHeight = useCallback((index: number) => {
    const baseHeight = 50;
    if (isLoading) return baseHeight;
    
    const folder = filteredFolders[index];
    if (!folder) return baseHeight;
    
    // Check if this folder's dropdown is expanded
    if (expandedFolders.has(folder.dossier_id)) {
      const subFolderCount = subfolderCounts[folder.dossier_id] || 0;

      if (subFolderCount > 1) {
        const containerPadding = 6; // Vertical padding of the sub-list container
        const itemHeight = 39;      // The height of a single subfolder item
        const dynamicHeight = containerPadding + (subFolderCount * itemHeight);
        
        return baseHeight + dynamicHeight;
      }
    }
    
    return baseHeight;
  }, [isLoading, filteredFolders, expandedFolders, subfolderCounts]);

  /**
   * @description Reset item heights cache when selected folder changes
   */
  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [expandedFolders]);

  /**
   * @description Renders a row in the FoldersList component. Essential for the react-window VariableSizeList component.
   */
  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      if (isLoading) {
        return (
          <div style={style}>
            <ListItemButton sx={{ opacity: 1 - index * 0.1 }}>
              <Grid container alignItems="center" spacing={1}>
                {/* First column skeleton */}
                <Grid item xs={7}>
                  <Stack spacing={0.5}>
                    <Skeleton animation="wave" variant="text" width="90%" height={20} />
                    <Skeleton animation="wave" variant="text" width="70%" height={14} />
                  </Stack>
                </Grid>

                {/* Second column skeleton */}
                <Grid item xs={3}>
                  <Stack alignItems="flex-end" spacing={0.5}>
                    <Skeleton animation="wave" variant="text" width="80%" height={20} />
                    <Skeleton animation="wave" variant="text" width="60%" height={16} />
                  </Stack>
                </Grid>

                {/* Third column skeleton */}
                <Grid item xs={2} sx={{ textAlign: "right", pl: 0 }}>
                  <Skeleton animation="wave" variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
                </Grid>
              </Grid>
            </ListItemButton>
            <Divider sx={{ width: "60%", mx: "auto", borderColor: "grey.300", opacity: 1  }} />
          </div>
        );
      }

      const folder = filteredFolders[index];
      return (
        <div style={style}>
          <FolderItem
            folder={folder}
            onClick={() => {
              setSelectedFolder(selectedFolderId === folder.dossier_id ? undefined : folder);
              setSelectedFolderId(selectedFolderId === folder.dossier_id ? null : folder.dossier_id);
              setHighlightedSubFolder(null);
            }}
            selected={folder.dossier_id === selectedFolder?.dossier_id}
            onConfigureAutoImport={handleConfigureAutoImport}
            expandedFolders={expandedFolders}
            onToggleExpanded={handleToggleExpanded}
            onSelectedSousDossier={handleSelectedSousDossier}
            highlightedSubFolder={highlightedSubFolder}
          />
          <Divider sx={{ width: "6%", mx: "auto", mt: 2, borderWidth: "-0.9px", borderColor: "#264C9D", opacity: 1}} />
        </div>
      );
    },
    [filteredFolders, isLoading, selectedFolder, handleConfigureAutoImport, expandedFolders, handleToggleExpanded, handleSelectedSousDossier, highlightedSubFolder]
  );

  return (
    <Box sx={styles.pageContainer}>


      {/* Access blocked message */}
      {foldersData?.client_blocked && (
        <Box
          sx={styles.blockedMessageContainer}
        >
          <Box
            sx={styles.blockedReasonText}
          >
            <Typography variant="h6" color="primary" fontWeight="bold">
              {foldersData.client_blocked_reason || "Accès bloqué"}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Dossiers search bar */}
      <Box sx={styles.searchContainer}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            autoFocus
            sx={styles.searchField}
            fullWidth
            size="small"
            variant="outlined"
            margin="none"
            placeholder="Rechercher un dossier ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              size: "small",
              endAdornment: search && (
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseOutlined />
                </IconButton>
              ),
            }}
          />
          <Tooltip title="Actualiser la liste des dossiers">
            <IconButton 
              size="small" 
              onClick={() => refetchFolders()}
              disabled={isLoading}
              sx={{ 
                minWidth: 'auto',
                padding: '0px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Dossiers filter checkboxes */}
        <Stack pl={2.5} mt={0.5} mb={0} direction="row">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showMyFolders}
                color="primary"
                onChange={(e) => setShowMyFolders(e.target.checked)}
                sx={{ mr: 0.5, p:0, borderRadius: "2px" }}
                icon={UncheckedIcon}
                checkedIcon={CheckedIcon}
              />
              }
              label={<Typography sx={{
                color: "#1C252E",
                fontFamily: "Inter",
                fontSize: "0.6875rem",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "1.375rem",
                letterSpacing: "0rem",
              }}>Mes dossiers</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={showInProgress}
                onChange={(e) => setShowInProgress(e.target.checked)}
                sx={{ mr: 0.5, p:0, borderRadius: "2px" }}
                icon={UncheckedIcon}
                checkedIcon={CheckedIcon}
              />
            }
            label={<Typography sx={{
              color: "#1C252E",
              fontFamily: "Inter",
              fontSize: "0.6875rem",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "1.375rem",
              letterSpacing: "0rem",
            }}>En cours</Typography>}
            sx={{ ml: 0.5 }}
          />
        </Stack>
      </Box>

      {/* Folders list */}
      {(isLoading || !!filteredFolders.length) && (
        <Box ref={listContainerRef} sx={styles.listContainer}>
          <StyledList
            ref={listRef}
            height={listHeight}
            itemSize={getItemHeight}
            itemCount={isLoading ? 8 : filteredFolders.length}
            width="100%"
          >
            {Row}
          </StyledList>
        </Box>
      )}

      {/* Folders list if content is empty */}
      {!isLoading && !filteredFolders.length && (
        <List
          sx={{
            ...styles.listStyles,
            height: "100%",
          }}
        >
          <NoFolders />
        </List>
      )}

      {/* Bottom action bar (Pieces-jointes, Glissez-déposez, Espace notarial) */}
      <Box sx={styles.buttonsGrid}>
        <Grid container spacing={{ xs: 1 }} columns={{ xs: 3 }} sx={{ ...styles.buttonsContainer, justifyContent: "space-between" }}>
          <Grid item xs={1} sx={styles.buttonItem}>
            <WarningTooltip
              title={
                !selectedFolder
                  ? "Vous devez sélectionner un sous-dossier"
                  : hasAttachments
                    ? ""
                    : "Aucune pièce jointe disponible"
              }
            >
              <span>
                <Button
                  fullWidth
                  disabled={!selectedFolder || !hasAttachments || !selectedSubFolder?.[0]?.sous_dossier_id}
                  onClick={() => navigate(`/folder/${selectedFolder?.dossier_id}/attachment?sousDossierId=${highlightedSubFolder?.sous_dossier_id ?? selectedSubFolder?.[0]?.sous_dossier_id ?? ''}`)}
                  sx={size?.width ? {
                    ...styles.bottomNavButtons(size.width, (!selectedFolder || !hasAttachments || !selectedSubFolder?.[0]?.sous_dossier_id)),
                    height: '50px',
                    minHeight: '50px',
                  } : {}}
                >
                  <Typography color="white" fontWeight="normal" sx={{ fontSize: '12px' }}>
                    Pièces jointes
                  </Typography>
                </Button>
              </span>
            </WarningTooltip>
          </Grid>
          <Grid item xs={1} sx={styles.buttonItem}>
            <WarningTooltip title={!selectedFolder ? "Vous devez sélectionner un sous-dossier" : ""}>
              <span>
                <Button
                  fullWidth
                  disabled={!selectedFolder || !selectedSubFolder?.[0]?.sous_dossier_id}
                  onClick={() => navigate(`/folder/${selectedFolder?.dossier_id}/drag-and-drop?sousDossierId=${highlightedSubFolder?.sous_dossier_id ?? selectedSubFolder?.[0]?.sous_dossier_id ?? ''}`)}
                  sx={size?.width ? {
                    ...styles.bottomNavButtons(size.width, (!selectedFolder || !selectedSubFolder?.[0]?.sous_dossier_id)),
                    height: '50px',
                    minHeight: '50px',
                  } : {}}
                >
                  <Typography color="white" fontWeight="normal" sx={{ fontSize: '12px' }}>
                    Glissez-déposez
                  </Typography>
                </Button>
              </span>
            </WarningTooltip>
          </Grid>
          <Grid item xs={1} sx={styles.buttonItem}>
            <WarningTooltip title={!selectedFolder ? "Vous devez sélectionner un sous-dossier" : ""}>
              <span>
                <Button
                  fullWidth
                  disabled={!selectedFolder || !selectedSubFolder?.[0]?.sous_dossier_id}
                  onClick={() => navigate(`/folder/${selectedFolder?.dossier_id}/notary-space?sousDossierId=${highlightedSubFolder?.sous_dossier_id ?? selectedSubFolder?.[0]?.sous_dossier_id ?? ''}`)}
                  sx={size?.width ? {
                    ...styles.bottomNavButtons(size.width, (!selectedFolder || !selectedSubFolder?.[0]?.sous_dossier_id)),
                    height: '50px',
                    minHeight: '50px',
                  } : {}}
                >
                  <Typography color="white" fontWeight="normal" sx={{ fontSize: '12px' }}>
                    Espace-notarial
                  </Typography>
                </Button>
              </span>
            </WarningTooltip>
          </Grid>
        </Grid>
        <Grid item xs={3} sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 0,
          padding: 1,
          width: '100%' }}>
            <WarningTooltip title={!selectedFolder || !selectedSubFolder?.[0]?.sous_dossier_id ? "Vous devez sélectionner un sous-dossier" : ""}>
              <span>
              <AiButton variant={!selectedFolder || !selectedSubFolder?.[0]?.sous_dossier_id ? "disabled" : "default"} onClick={() => setIsModalOpen(true)} />
            </span>
          </WarningTooltip>
          <PaletteSousDossier
            selectedFolderId={selectedFolderId as string}
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            items={selectedSubFolder || []}
            onItemActionClick={handleItemAction}
            onItemStatusUpdateRequest={handleSubFolderIndexRequest}
            />
        </Grid>
      </Box>

      {/* Select partenaire modal */}
      {shouldOverrideRenderInitialesDialog ?
        (
          <SelectInitialesDialog
            id="select-initiales-dialog"
            open={!!displayModal}
            keepMounted={false}
            setPollingConfig={setPollingConfig}
            startPolling={startPolling}
            isPolling={isPolling}
          />
        )
        :
        null
      }

      {/* MOVED TO LAYOUT: Notifications drawer */}
      {/* <Drawer anchor='right' open={drawerOpen} onClose={() => setDrawerOpen(false)} hideBackdrop
        PaperProps={{
          sx: { width: "90%", maxWidth: 400, backgroundColor: "rgb(255, 255, 255, 0.8)", borderTopLeftRadius: 4, borderTopRightRadius: 0, borderBottomLeftRadius: 4, borderBottomRightRadius: 0 },
        }}
      >
          <Box sx={{ width: '100%', backgroundColor: "transparent" }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}>
            <Typography color="primary" variant="h6" fontWeight="bold" sx={{fontSize: '14px' }}>
              Dossier(s) en attente de choix
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseOutlined sx={{ "&:hover": { color: "error.main" } }} />
            </IconButton>
            </Box>

          <List dense disablePadding sx={{ }}>
            {sousDossierWithChoiceFiliationNeededList?.map((sous_dossier) => (
              <ListItemButton key={`sous-dossier-${sous_dossier.sous_dossier_id}-${sous_dossier.dossier_id}`} divider disableGutters alignItems="flex-start" dense onClick={() => navigate(`/folder/${sous_dossier.dossier_id}/filiation/${sous_dossier.sous_dossier_id}`)} sx={{ "&:hover": { backgroundColor: "rgba(27,72,158,0.05)" } }}>
                <ListItem dense alignItems="flex-start" sx={{ backgroundColor: "transparent" }}
                  secondaryAction={
                    <IconButton edge="end" sx={{ color: "#1C252E" }}>
                      <LaunchIcon />
                    </IconButton>
                  }>
                    <ListItemText
                      primary={
                        <>
                          <Box sx={{ m: 0 }}>
                              <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{
                                      marginBottom: -0.5,
                                      overflow: "hidden",
                                      fontSize: "11px",
                                      textOverflow: "ellipsis",
                                      color: "#1C252E"
                                  }}
                                  title={sous_dossier.dossier_id}
                              >
                                  {sous_dossier.dossier_id ?? ""}
                              </Typography>
                              <Typography
                                  variant="subtitle2"
                                  sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      color: "#1C252E"
                                  }}
                                  title={sous_dossier.dossier_name}
                              >
                                  {sous_dossier.dossier_name ?? ""}
                              </Typography>
                              </Box>
                        </>
                      }
                      secondary={
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ color: '#637381', display: 'inline', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', fontSize: '12px' }}>
                            {sous_dossier.sous_dossier_name}
                          </Typography>
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{ color: '#637381', display: 'inline', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', fontSize: '12px' }}
                              title={sous_dossier.en_attente_choix_filiation_sous_dossier_by_details?.map(detail => detail.email).join(', ') || 'Aucun email'}
                          >
                            {sous_dossier.en_attente_choix_filiation_sous_dossier_by_details?.map(detail => detail.email.split('@')[0]).join(', ') || 'Aucun email'}
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
              </ListItemButton>
            ))}
          </List>
        </Box>
        <Box sx={{ backgroundColor: "rgb(255, 255, 255, 0.15)", backdropFilter: "blur(4px)", position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: -1, height: `calc(6.7rem * ${sousDossierWithChoiceFiliationNeededList?.length})` }}></Box>
      </Drawer> */}
    </Box>
  );
}

interface SousDossierAutoImportListProps {
  open: boolean;
  onClose: () => void;
  folder: Folder | undefined;
  subFolders: SubFolder[] | undefined;
  onSelectedSousDossier: (sousDossier: SubFolder) => void;
  highlightedSubFolder: SubFolder | null;
}

const SousDossierAutoImportList: React.FC<SousDossierAutoImportListProps> = ({
  open,
  onClose,
  folder,
  subFolders,
  onSelectedSousDossier,
  highlightedSubFolder,
}) => {
  const localSubFolderStatuses = useMemo<Record<string, boolean>>(() => {
    if (!subFolders) {
      return {};
    }
    return subFolders.reduce((acc, sf) => {
      acc[sf.sous_dossier_id] = !!sf.sous_dossier_import_partenaire_auto;
      return acc;
    }, {} as Record<string, boolean>);
  }, [subFolders]);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const navigate = useNavigate();
  const [highlightedSubFolderId, setHighlightedSubFolderId] = useState<string | null>(highlightedSubFolder?.sous_dossier_id ?? null);
  

  const setSousDossierAutoImportStatusMutation = useSetSousDossierAutoImportStatus({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getSubFolders', folder?.dossier_id] });
      queryClient.invalidateQueries({ queryKey: ['getFolders'] });
    },
    onError: (error) => {
      enqueueSnackbar(
        typeof error === "string"
          ? error
          : (error?.message ?? "Une erreur est survenue lors de la mise a jour du statut d'import automatique"),
        {
          variant: "error",
          persist: true,
          action: (snackbarId: SnackbarKey | undefined) => (
            <SnackbarCloseIcon handleClose={() => closeSnackbar(snackbarId)} />
          ),
        }
      );
    },
  });

  

  if (!open) {
    return null;
  }

  return (
    <Box sx={{
      backgroundColor: '#F2F4FA',
      boxShadow: `
        inset 0 4px 6px -3px rgba(0, 0, 0, 0.3), 
        inset 4px 0 6px -3px rgba(0, 0, 0, 0.3), 
        inset -4px 0 6px -3px rgba(0, 0, 0, 0.3)
      `,
      mx: -1, 
      my: 0,
      p: '0.5px',
      borderRadius: '4px',
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    }}>
      {!subFolders ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <List dense disablePadding>
          {subFolders.map((sf, index) => {
            const [isChecked, setIsChecked] = useState(localSubFolderStatuses[sf.sous_dossier_id] ?? false);

            // useEffect(() => {
            //   if (localSubFolderStatuses[sf.sous_dossier_id] !== isChecked) {
            //     setIsChecked(localSubFolderStatuses[sf.sous_dossier_id] ?? false);
            //   }
            // }, [localSubFolderStatuses[sf.sous_dossier_id]]);

            const handleToggle = (sousDossierId: string) => {
              const newLocalSubFolderStatuses = {
                ...localSubFolderStatuses,
                [sousDossierId]: !localSubFolderStatuses[sousDossierId],
              };
              
              if (folder) {
                setSousDossierAutoImportStatusMutation.mutate({
                  dossierId: folder.dossier_id,
                  sousDossierId: sousDossierId,
                  autoImportStatusValue: newLocalSubFolderStatuses[sousDossierId],
                });
              }
              setIsChecked(!isChecked);
            };

            return (
              <React.Fragment key={sf.sous_dossier_id}>
                <ListItemButton
                  sx={{
                  ...styles.listSubFolderItemButton(theme, alpha),
                  background : highlightedSubFolderId === sf.sous_dossier_id ?
                  'linear-gradient(99deg, #DBE7FF 3.31%, #E9D8FF 96.69%) !important' : 'transparent',
                  boxShadow: highlightedSubFolderId === sf.sous_dossier_id ? index === 0 ? 'inset 0 4px 6px -3px rgba(0, 0, 0, 0.3), inset 4px 0 6px -3px rgba(0, 0, 0, 0.3), inset -4px 0 6px -3px rgba(0, 0, 0, 0.3)' : 'inset 6px 0 6px -4px rgba(0,0,0,0.3), inset -6px 0 6px -4px rgba(0,0,0,0.3)' : 'none',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.02)',
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onSelectedSousDossier(sf);
                }}
              >
                <Grid container alignItems="center">
                  {/* First column: Switch and name */}
                  <Grid item xs={9} sx={{ pl: '1px' }}>
                    <Stack direction="row" alignItems="center" spacing={1} m={'-2px'}>
                      <Box sx={{ p: 1, display: 'inline-flex', alignItems: 'center' }}>
                        <StyledTooltipIcon placement="bottom" tooltipTitle={`${localSubFolderStatuses[sf.sous_dossier_id] === false ? 'Activer l’analyse automatique' : 'Désactiver l’analyse automatique'}`}>
                          <AutoImportStatusSwitch
                          checked={isChecked}
                          sx={{
                            ...(isChecked === false && {
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: theme.palette.warning.main,
                              },
                            }),
                          }}
                          onChange={() => handleToggle(sf.sous_dossier_id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        </StyledTooltipIcon>
                      </Box>

                      <Stack ml={'-2px !important'}>
                        {/* Subfolder name */}
                        <Stack direction="row">
                          <Tooltip title={sf.name?.length > 30 ? 
                            <Typography variant="caption" color="white">
                              {sf.name} {sf.nature}
                            </Typography> : undefined}>
                            <Typography
                              variant="caption"
                              sx={{
                                overflow: "hidden",
                                color: "rgba(9, 34, 107, 0.90)",
                                textOverflow: "ellipsis",
                                fontFamily: "Inter, Helvetica",
                                fontSize: "0.5625rem",
                                fontStyle: "normal",
                                fontWeight: 500,
                                lineHeight: "normal",
                              }}
                            >
                              {sf.nature ?? "N/A"} {sf.name ?? "N/A"}
                            </Typography>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Grid>

                  {/* Second column: Status and ID */}
                  <Grid item xs={3} sx={{ pl: 0 }}>
                    <Grid container alignItems="center" justifyContent="flex-end" spacing={1} wrap="nowrap">
                      <Grid item mr={1}>
                        <Stack alignItems="flex-end">
                          {sf.en_attente_choix_filiation_sous_dossier && (
                            <Tooltip title="En attente de sélection des biens">
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-block',
                                  width: 8,
                                  height: 8,
                                  backgroundColor: 'orange',
                                  borderRadius: '50%',
                                  marginLeft: 1,
                                  alignSelf: 'center',
                                }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </Grid>
                      <Grid item>
                        <Stack direction="row" alignItems="flex-end">
                        <StyledTooltipIcon placement="bottom" tooltipTitle="Ouvrir le centre de contrôle">
                          <IconButton
                            size="large"
                            color="inherit"
                            sx={styles.controlCenterIcon(theme)}
                            edge="start"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/folder/${folder?.dossier_id}/details/${sf.sous_dossier_id}`);
                            }}
                          >
                            <BsGear color={folder?.en_attente_choix_filiation_dossier ? "warning" : "inherit"} fontSize="small" size={20} />
                          </IconButton>
                        </StyledTooltipIcon>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </ListItemButton>
              {index < subFolders.length - 1 && (
                <Divider sx={{ mx: 2, borderColor: 'rgba(0, 0, 0, 0.08)'}} />
              )}
            </React.Fragment>
            );
          })}
        </List>
      )}
    </Box>
  );
};
